import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  type BackgroundImageId,
  findGradientPreset,
  GRADIENT_PRESETS,
  gradientCss,
  imageUrlFor,
  PRESET_IMAGE_IDS,
  presetImageUrl,
  SOLID_COLOR_SWATCHES,
  type UserImageSlot,
  USER_IMAGE_SLOTS,
} from "@/lib/dashboard-home/background";
import { cn } from "@/lib/utils";
import { Check, Loader2, Upload } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import type { BackgroundDraft } from "./use-background-draft";

type OptionProps = {
  draft: BackgroundDraft;
  update: (patch: Partial<BackgroundDraft>) => void;
};

/** Ligne d'option (radio) : libellé à gauche, aperçu à droite. */
const OptionRow = ({
  selected,
  onSelect,
  title,
  extra,
  preview,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  extra?: ReactNode;
  preview: ReactNode;
}) => (
  <div
    role="radio"
    aria-checked={selected}
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect();
      }
    }}
    className={cn(
      "flex cursor-pointer items-center justify-between gap-4 rounded-lg border px-3 py-2.5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary",
      selected
        ? "border-transparent bg-brand-50"
        : "border-slate-200 hover:bg-slate-50",
    )}
  >
    <div className="flex min-w-0 flex-col items-start gap-1">
      <span
        className={cn(
          "text-sm font-medium",
          selected ? "text-primary" : "text-slate-900",
        )}
      >
        {title}
      </span>
      {extra}
    </div>
    <div className="shrink-0">{preview}</div>
  </div>
);

const Swatch = ({
  selected,
  className,
  style,
  children,
}: {
  selected?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}) => (
  <span
    className={cn(
      "block h-9 w-16 overflow-hidden rounded-md border",
      selected ? "border-primary ring-1 ring-primary" : "border-slate-300",
      className,
    )}
    style={style}
  >
    {children}
  </span>
);

/**
 * Aperçu cliquable qui ouvre un choix (couleurs, dégradés, images). Le choix
 * est rendu hors de la modale (portail) pour passer au-dessus d'elle au lieu
 * d'être rogné ou de la faire défiler. React fait pourtant remonter ses clics
 * jusqu'à la ligne : on les arrête, sinon la ligne réécrirait le brouillon
 * avec l'ancienne valeur.
 */
const SwatchPicker = ({
  label,
  swatch,
  onOpen,
  className,
  children,
}: {
  label: string;
  swatch: ReactNode;
  onOpen: () => void;
  className?: string;
  children: ReactNode;
}) => (
  <Popover>
    <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
      <button type="button" aria-label={label} onClick={onOpen}>
        {swatch}
      </button>
    </PopoverTrigger>
    <PopoverPortal>
      <PopoverContent
        align="end"
        // Au-dessus de la modale (z-50) et de ses variantes empilées (z-60).
        className={cn("z-[70] p-3", className)}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </PopoverContent>
    </PopoverPortal>
  </Popover>
);

/** Pastille d'une image du choix : fournie, ou emplacement personnel (vide, rempli ou en cours de mesure). */
const ImageThumb = ({
  url,
  label,
  selected,
  onSelect,
  onReplace,
}: {
  url: string | null;
  label: string;
  selected: boolean;
  onSelect: () => void;
  /** Emplacement personnel rempli : remplacer son image. */
  onReplace?: () => void;
}) => {
  const vt = useVisibleT();
  return (
    <div className="flex min-w-0 flex-col items-center gap-1 text-xs">
      <div className="relative w-full">
        <button
          type="button"
          title={label}
          aria-label={label}
          aria-pressed={selected}
          onClick={onSelect}
          className="block w-full"
        >
          <Swatch
            selected={selected}
            className={cn(
              "h-12 w-full",
              !url &&
                "flex items-center justify-center border-dashed text-lg text-slate-400",
            )}
            style={
              url
                ? {
                    backgroundImage: `url("${url}")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }
                : undefined
            }
          >
            {!url && "+"}
          </Swatch>
        </button>
        {url && onReplace && (
          <button
            type="button"
            title={vt("bo.home.configure.replaceImage", "Remplacer l’image")}
            aria-label={vt(
              "bo.home.configure.replaceImage",
              "Remplacer l’image",
            )}
            onClick={onReplace}
            className="absolute right-1 top-1 flex size-5 items-center justify-center rounded bg-white/90 text-slate-700 shadow-sm hover:bg-white"
          >
            <Upload className="size-3" aria-hidden />
          </button>
        )}
      </div>
      <span className="w-full truncate text-center">{label}</span>
    </div>
  );
};

export const ImageOption = ({
  draft,
  analyzing,
  onSelect,
  onSelectImage,
  onPickFile,
}: {
  draft: BackgroundDraft;
  analyzing: boolean;
  /** Clic sur la ligne : retient l'image courante du brouillon. */
  onSelect: () => void;
  onSelectImage: (imageId: BackgroundImageId) => void;
  /** Ouvre le choix d'un fichier pour cet emplacement personnel. */
  onPickFile: (slot: UserImageSlot) => void;
}) => {
  const vt = useVisibleT();
  const selected = draft.type === "image";
  const currentUrl = imageUrlFor(draft.imageId, draft.userImageUrls);
  const swatch = analyzing ? (
    <Swatch className="flex items-center justify-center">
      <Loader2 className="size-4 animate-spin text-slate-400" />
    </Swatch>
  ) : (
    <Swatch
      selected={selected}
      style={
        currentUrl
          ? {
              backgroundImage: `url("${currentUrl}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : undefined
      }
    />
  );

  return (
    <OptionRow
      selected={selected}
      onSelect={onSelect}
      title={vt("bo.home.configure.images", "Sélectionner une image")}
      preview={
        <SwatchPicker
          label={vt("bo.home.configure.pickImage", "Choisir l’image")}
          onOpen={onSelect}
          className="w-[320px] max-w-[calc(100vw-2rem)]"
          swatch={swatch}
        >
          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold text-slate-500">
              {vt("bo.home.configure.presetImages", "Images proposées")}
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {PRESET_IMAGE_IDS.map((id, index) => (
                <ImageThumb
                  key={id}
                  url={presetImageUrl(id)}
                  label={vt(
                    `bo.home.configure.presetImage${index + 1}`,
                    `Image ${index + 1}`,
                  )}
                  selected={selected && draft.imageId === id}
                  onSelect={() => onSelectImage(id)}
                />
              ))}
            </div>
          </section>
          <section className="mt-4 flex flex-col gap-2">
            <h3 className="text-xs font-semibold text-slate-500">
              {vt("bo.home.configure.myImages", "Mes images")}
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {USER_IMAGE_SLOTS.map((slot) => {
                const id = `user-${slot}` as const;
                const url = draft.userImageUrls[slot - 1] ?? null;
                return (
                  <ImageThumb
                    key={id}
                    url={url}
                    label={vt(
                      `bo.home.configure.userImage${slot}`,
                      `Mon image ${slot}`,
                    )}
                    selected={selected && draft.imageId === id}
                    // Un emplacement vide ouvre directement le choix du fichier.
                    onSelect={() =>
                      url ? onSelectImage(id) : onPickFile(slot)
                    }
                    onReplace={() => onPickFile(slot)}
                  />
                );
              })}
            </div>
            <p className="text-xs text-slate-500">
              {vt(
                "bo.home.configure.imageHint",
                "JPEG, PNG ou WebP, 2560 × 1440 px conseillés.",
              )}
            </p>
          </section>
        </SwatchPicker>
      }
    />
  );
};

export const ColorOption = ({ draft, update }: OptionProps) => {
  const vt = useVisibleT();
  const selected = draft.type === "color";
  return (
    <OptionRow
      selected={selected}
      onSelect={() => update({ type: "color" })}
      title={vt("bo.home.configure.color", "Sélectionner une couleur unie")}
      preview={
        <SwatchPicker
          label={vt("bo.home.configure.pickColor", "Choisir la couleur")}
          onOpen={() => update({ type: "color" })}
          className="w-auto"
          swatch={
            <Swatch
              selected={selected}
              style={{ backgroundColor: draft.color }}
            />
          }
        >
          <div className="grid grid-cols-4 gap-2">
            {SOLID_COLOR_SWATCHES.map((color) => (
              <button
                key={color}
                type="button"
                aria-label={color}
                onClick={() => update({ type: "color", color })}
                className="relative size-8 rounded-md border border-slate-200"
                style={{ backgroundColor: color }}
              >
                {draft.color.toLowerCase() === color && (
                  <Check className="absolute inset-0 m-auto size-4 text-primary" />
                )}
              </button>
            ))}
          </div>
          <label className="mt-3 flex items-center justify-between gap-3 text-sm">
            {vt("bo.home.configure.customColor", "Autre couleur")}
            <input
              type="color"
              value={draft.color}
              onChange={(e) => update({ type: "color", color: e.target.value })}
              className="h-8 w-14 cursor-pointer rounded border border-slate-200 bg-transparent"
            />
          </label>
        </SwatchPicker>
      }
    />
  );
};

export const GradientOption = ({ draft, update }: OptionProps) => {
  const vt = useVisibleT();
  const selected = draft.type === "gradient";
  return (
    <OptionRow
      selected={selected}
      onSelect={() => update({ type: "gradient" })}
      title={vt("bo.home.configure.gradient", "Sélectionner un dégradé")}
      preview={
        <SwatchPicker
          label={vt("bo.home.configure.pickGradient", "Choisir le dégradé")}
          onOpen={() => update({ type: "gradient" })}
          className="w-[264px] max-w-[calc(100vw-2rem)]"
          swatch={
            <Swatch
              selected={selected}
              style={{
                backgroundImage: gradientCss(
                  findGradientPreset(draft.gradientId),
                ),
              }}
            />
          }
        >
          <div className="grid grid-cols-3 gap-3">
            {GRADIENT_PRESETS.map((preset) => {
              const label = vt(preset.labelKey, preset.label);
              return (
                <button
                  key={preset.id}
                  type="button"
                  title={label}
                  onClick={() =>
                    update({ type: "gradient", gradientId: preset.id })
                  }
                  className="flex min-w-0 flex-col items-center gap-1 text-xs"
                >
                  <Swatch
                    selected={draft.gradientId === preset.id}
                    style={{ backgroundImage: gradientCss(preset) }}
                  />
                  <span className="w-full truncate text-center">{label}</span>
                </button>
              );
            })}
          </div>
        </SwatchPicker>
      }
    />
  );
};

export const NoneOption = ({ draft, update }: OptionProps) => {
  const vt = useVisibleT();
  return (
    <OptionRow
      selected={draft.type === "none"}
      onSelect={() => update({ type: "none" })}
      title={vt("bo.home.configure.none", "Aucun fond")}
      preview={
        <Swatch className="bg-slate-100">
          <svg
            className="size-full"
            viewBox="0 0 95 52"
            preserveAspectRatio="none"
            aria-hidden
          >
            <line x1="6" y1="46" x2="89" y2="6" stroke="#94a3b8" />
          </svg>
        </Swatch>
      }
    />
  );
};
